import React from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { useWatch, useFormState, Controller } from "react-hook-form";

const PEST_OPTIONS = ["Roaches","Mosq","Rodents","Flies","Ants","Others"];

export default function FindingsCard({ control, register, index, removeFinding }) {
  // Watch the object of pest counts for this specific card
  const pestsObj = useWatch({ control, name: `findingsAreas.${index}.pests` }) || {};

  // surface errors for this row
  const { errors } = useFormState({ control });
  const rowErrors = errors?.findingsAreas?.[index] || {};

  // Compute total (optional – handy for QA)
  const totalCount = PEST_OPTIONS.reduce((sum, key) => {
    const n = Number(pestsObj?.[key] ?? 0);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          SPECIFY AREA/S
        </Typography>

        {/* Location */}
        <TextField
          label="Location"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          {...register(`findingsAreas.${index}.location`, {
            required: "Location is required",
          })}
          error={!!rowErrors?.location}
          helperText={rowErrors?.location?.message || ""}
        />

        {/* Hidden validator: require at least one pest > 0 */}
        <Controller
          name={`findingsAreas.${index}.pests`}
          control={control}
          rules={{
            validate: (val) => {
              const obj = val || {};
              const sum = PEST_OPTIONS.reduce((s, k) => {
                const v = Number(obj?.[k]);
                return s + (isNaN(v) ? 0 : v);
              }, 0);
              return sum > 0 || "Enter a count for at least one pest type (> 0).";
            },
          }}
          render={() => null}
        />

        {/* Six pest inputs side-by-side (wrap on mobile) */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
          {PEST_OPTIONS.map((pest) => (
            <TextField
              key={pest}
              label={pest}
              type="number"
              size="small"
              sx={{ width: 120, minWidth: 100 }}
              defaultValue={0}
              inputProps={{ min: 0 }}
              {...register(`findingsAreas.${index}.pests.${pest}`, { valueAsNumber: true })}
            />
          ))}
        </Box>

        {/* Optional total */}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 0.5, color: rowErrors?.pests ? "error.main" : "inherit" }}
        >
          Total Pest Count: {totalCount}
        </Typography>
        {rowErrors?.pests && (
          <Typography variant="caption" color="error">
            {rowErrors?.pests?.message}
          </Typography>
        )}

        {/* Remove button (if parent passes it) */}
        {removeFinding && (
          <Box textAlign="right">
            <Button color="error" size="small" onClick={() => removeFinding(index)}>
              Remove Area
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
